import { PrioritizationMode, QueueCadenceFormValues, QueueCadenceSettings } from './WorkflowBuilder.types';

/**
 * PRD defaults, applied as initial form values so every workflow created or
 * saved through this form carries them explicitly. The backend reads an absent
 * field as "feature off", which is what pre-existing campaigns keep until
 * someone saves them here.
 */
export const QUEUE_CADENCE_DEFAULTS: QueueCadenceFormValues = {
    prioritizationMode: 'cadence_completeness',
    maxCallsPerDay: 3,
    sameDayEnforcement: true,
    businessDayEnd: '18:00',
    queueDelayAlertThreshold: 10,
    retryTimeShiftEnabled: true,
};

export const MAX_CALLS_PER_DAY_MIN = 1;
export const MAX_CALLS_PER_DAY_MAX = 10;
export const ALERT_THRESHOLD_MIN = 0;
export const ALERT_THRESHOLD_MAX = 100;

export const PRIORITIZATION_MODE_OPTIONS: {
    value: PrioritizationMode;
    label: string;
    description: string;
}[] = [
    {
        value: 'cadence_completeness',
        label: 'Cadence completeness',
        description: 'Parties already in the cadence are called before parties with no touchpoints yet.',
    },
    {
        value: 'even_distribution',
        label: 'Even distribution',
        description: 'Outreach is spread across parties: every first call before any second call.',
    },
];

/** Copy shared by the disabled day-boundary controls and their tooltip. */
export const TIMEZONE_REQUIREMENT_MESSAGE =
    'Set an operating hours timezone first: the daily cap, same-day enforcement and business day end all need one to anchor the end of a day.';

/**
 * The daily cap, same-day enforcement and the business day end are the three
 * day-boundary settings, and the backend rejects all three unless the workflow's
 * operating hours resolve to a real timezone. 'OTHER' is the placeholder the
 * timezone Select shows for "reach out to support", so it never resolves.
 */
export const hasOperatingHoursTimezone = (timezone?: string): boolean => !!timezone && timezone !== 'OTHER';

/** 24-hour operating hours start at midnight; otherwise the configured start time. */
export const effectiveOperatingHoursStart = (ctx: {
    operatingHoursEnabled: boolean;
    operatingHoursStartTime: string;
}): string => (ctx.operatingHoursEnabled ? '00:00' : ctx.operatingHoursStartTime);

/**
 * With same-day enforcement on, a business day end at or before the operating
 * hours start marks the whole operating window as past day end, which defers
 * every call forever. The backend rejects that combination too.
 */
export const validateBusinessDayEnd = (
    value: string,
    ctx: {
        sameDayEnforcement: boolean;
        operatingHoursEnabled: boolean;
        operatingHoursStartTime: string;
    }
): string | true => {
    if (!value) return 'Business day end is required';
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) return 'Business day end must be a 24-hour HH:MM time';
    if (!ctx.sameDayEnforcement) return true;

    const start = effectiveOperatingHoursStart(ctx);
    return value > start || 'Business day end must be later than the operating hours start time';
};

/**
 * Hydrates the six form fields from a stored config. Fields the workflow does
 * not carry are left out so the caller's defaults show through, mirroring
 * parseOperatingHoursFormData.
 */
export const parseQueueCadenceFormData = (qc?: QueueCadenceSettings): Partial<QueueCadenceFormValues> => {
    if (!qc) return {};
    return {
        ...(qc.prioritizationMode ? { prioritizationMode: qc.prioritizationMode } : {}),
        ...(typeof qc.maxCallsPerDay === 'number' ? { maxCallsPerDay: qc.maxCallsPerDay } : {}),
        ...(typeof qc.sameDayEnforcement === 'boolean' ? { sameDayEnforcement: qc.sameDayEnforcement } : {}),
        ...(qc.businessDayEnd ? { businessDayEnd: qc.businessDayEnd } : {}),
        ...(typeof qc.queueDelayAlertThreshold === 'number'
            ? { queueDelayAlertThreshold: qc.queueDelayAlertThreshold }
            : {}),
        ...(typeof qc.retryTimeShiftEnabled === 'boolean' ? { retryTimeShiftEnabled: qc.retryTimeShiftEnabled } : {}),
    };
};

/**
 * Builds the config payload. The three day-boundary settings are dropped when
 * no operating hours timezone resolves, because the backend rejects the whole
 * request in that case; the other three work without one.
 */
export const buildQueueCadencePayload = (
    values: QueueCadenceFormValues,
    ctx: { operatingHoursTimezone: string }
): QueueCadenceSettings => {
    const payload: QueueCadenceSettings = { prioritizationMode: values.prioritizationMode };

    if (Number.isFinite(values.queueDelayAlertThreshold)) {
        payload.queueDelayAlertThreshold = values.queueDelayAlertThreshold;
    }
    payload.retryTimeShiftEnabled = values.retryTimeShiftEnabled;

    if (hasOperatingHoursTimezone(ctx.operatingHoursTimezone)) {
        if (Number.isFinite(values.maxCallsPerDay)) payload.maxCallsPerDay = values.maxCallsPerDay;
        payload.sameDayEnforcement = values.sameDayEnforcement;
        if (values.businessDayEnd) payload.businessDayEnd = values.businessDayEnd;
    }

    return payload;
};
